import React, { useState, useMemo } from "react";
import "./ReportsSidebar.css";

/**
 * ReportsSidebar — hierarchical, searchable, categorised replacement
 * for the old horizontal <Slider> report selector.
 *
 * FE-1  Sidebar hierarchy
 * FE-3  Search + category chips
 * FE-9  Tooltip / description per report
 */

/* ── Category definitions (order matters) ── */
const CATEGORIES = [
  { key: "visits",      labelKey: "reports_cat_visits" },
  { key: "materials",   labelKey: "reports_cat_materials" },
  { key: "inventory",   labelKey: "reports_cat_inventory" },
  { key: "performance", labelKey: "reports_cat_performance" },
  { key: "admin",       labelKey: "reports_cat_admin" },
];

/* ── Report items grouped by category ── */
const REPORT_ITEMS = [
  // ── Visits ──
  { key: "visits_frequeny_report",            category: "visits",      tooltipKey: "reports_tooltip_visits_frequeny" },
  { key: "target_visits_report_with_date",    category: "visits",      tooltipKey: "reports_tooltip_target_visits_with_date" },
  { key: "hospital",                          category: "visits",      tooltipKey: "reports_tooltip_hospital" },
  { key: "target_visits_report",              category: "visits",      tooltipKey: "reports_tooltip_target_visits" },

  // ── Materials ──
  { key: "free_samples_summarized_report",    category: "materials",   tooltipKey: "reports_tooltip_free_samples_summarized" },
  { key: "free_samples_detailed_report",      category: "materials",   tooltipKey: "reports_tooltip_free_samples_detailed" },
  { key: "marketing_tools_report",            category: "materials",   tooltipKey: "reports_tooltip_marketing_tools" },
  { key: "objection_rebort",                  category: "materials",   tooltipKey: "reports_tooltip_objection" },
  { key: "monthly_orders_request_report",     category: "materials",   tooltipKey: "reports_tooltip_monthly_orders_request" },
  { key: "monthly_orders_report",             category: "materials",   tooltipKey: "reports_tooltip_monthly_orders" },

  // ── Inventory ──
  { key: "get_products_stock_report",         category: "inventory",   tooltipKey: "reports_tooltip_products_stock" },
  { key: "get_markitings_stock_report",       category: "inventory",   tooltipKey: "reports_tooltip_markitings_stock" },
  { key: "inventory_balance_card",            category: "inventory",   tooltipKey: "reports_tooltip_inventory_balance" },

  // ── Performance ──
  { key: "products_monthly_salesReport",      category: "performance", tooltipKey: "reports_tooltip_products_sales" },
  { key: "medical_rep_target_report",         category: "performance", tooltipKey: "reports_tooltip_medical_rep_target" },

  // ── Admin ──
  { key: "statisticsWithSearch",              category: "admin",       tooltipKey: "reports_tooltip_statistics" },
];

/* ── Component ── */
function ReportsSidebar({ activeReport, onSelect, translate, langmiamed }) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [collapsed, setCollapsed] = useState(true); // mobile: start collapsed

  /* Filter items by search text + active category */
  const filteredItems = useMemo(() => {
    let items = REPORT_ITEMS;

    if (activeCategory !== "all") {
      items = items.filter((i) => i.category === activeCategory);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter((i) => {
        const name = translate(i.key).toLowerCase();
        const desc = translate(i.tooltipKey).toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }

    return items;
  }, [search, activeCategory, translate]);

  /* Group filtered items by category (preserving CATEGORIES order) */
  const grouped = useMemo(() => {
    const map = {};
    for (const item of filteredItems) {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item);
    }
    return CATEGORIES.filter((c) => map[c.key]).map((c) => ({
      ...c,
      items: map[c.key],
    }));
  }, [filteredItems]);

  const dir = langmiamed === "ar" ? "rtl" : "ltr";

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="reports-sidebar-toggle"
        onClick={() => setCollapsed((p) => !p)}
        dir={dir}
      >
        <span className="las la-list"></span>
        {translate("reports_sidebar_toggle")}
      </button>

      <div
        className={`reports-sidebar${collapsed ? " collapsed" : ""}`}
        dir={dir}
      >
        {/* Search (FE-3) */}
        <input
          type="text"
          className="reports-search"
          placeholder={translate("reports_sidebar_search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Category chips (FE-3) */}
        <div className="reports-chips">
          <span
            className={`reports-chip${activeCategory === "all" ? " active" : ""}`}
            onClick={() => setActiveCategory("all")}
          >
            {translate("reports_cat_all")}
          </span>
          {CATEGORIES.map((cat) => (
            <span
              key={cat.key}
              className={`reports-chip${activeCategory === cat.key ? " active" : ""}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {translate(cat.labelKey)}
            </span>
          ))}
        </div>

        {/* Report list grouped by category (FE-1) */}
        <div className="reports-list">
          {grouped.length === 0 && (
            <div className="reports-no-results">
              {translate("reports_sidebar_no_results")}
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.key}>
              <div className="reports-cat-header">
                {translate(group.labelKey)}
              </div>
              {group.items.map((item) => (
                <div
                  key={item.key}
                  className={`reports-item${activeReport === item.key ? " active" : ""}`}
                  onClick={() => {
                    onSelect(item.key);
                    // collapse on mobile after selection
                    if (window.innerWidth <= 900) setCollapsed(true);
                  }}
                  title={translate(item.tooltipKey)}
                >
                  <span className="reports-item-name">
                    {translate(item.key)}
                  </span>
                  <span className="reports-item-desc">
                    {translate(item.tooltipKey)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default ReportsSidebar;
